// src/pages/HomePage.js
import React from 'react';
import styled from 'styled-components';
import { Helmet } from 'react-helmet-async';
import HeroSection from '../components/HeroSection';
import WhyTrustUs from '../components/WhyTrustUs';
import Features from '../components/Features';
import AppShowcase from '../components/AppShowcase';
import FAQSection from '../components/FAQSection';
import TokenTicker from '../components/TokenTicker';
import LiveResultsBanner from '../components/LiveResultsBanner';
import ClosedSignalsTicker from '../components/ClosedSignalsTicker';
import ActiveSignalsTicker from '../components/ActiveSignalsTicker';

const HomePageContainer = styled.div``;

const HomePage = () => {
  const schemaData = {
    "@context": "https://schema.org",
    "@type": "MobileApplication",
    "name": "Trend Genie — Free AI Crypto Signals App",
    "operatingSystem": "Android",
    "applicationCategory": "FinanceApplication",
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "5",
      "ratingCount": "104"
    },
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "description": "Trend Genie is the best free AI crypto signals app for Android. Get real-time AI crypto signals, automated trade setups, entry/stop-loss/take-profit levels, and DeFi safety scanning in one ecosystem.",
    "image": ["https://www.trendgenie.io/social-share-image.png"],
    "url": "https://www.trendgenie.io",
    "downloadUrl": "https://play.google.com/store/apps/details?id=com.entiresolutions.trendgenie",
    "author": {
      "@type": "Organization",
      "name": "Trend Genie",
      "url": "https://www.trendgenie.io"
    }
  };

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Trend Genie",
    "url": "https://www.trendgenie.io",
    "logo": "https://www.trendgenie.io/logo.png",
    "sameAs": [
      "https://x.com/trendgenie_ai"
    ],
    "description": "Trend Genie is the best crypto project combining a free AI crypto signals app, DeFi rug checker, Web3 social platform, and $TGEN utility token for traders and builders."
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "Is Trend Genie a free crypto signals app?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes. Trend Genie is a free AI crypto signals app available on Android with a 7-day full-access trial. It provides real-time AI-generated trade signals with entry, stop-loss, and take-profit levels."
        }
      },
      {
        "@type": "Question",
        "name": "What makes Trend Genie the best AI crypto signals app?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Trend Genie combines AI-powered crypto signals, live charting, DeFi token scanning (RugShield), a Web3 social platform, and the $TGEN utility token — making it one of the most complete free crypto signals apps available."
        }
      },
      {
        "@type": "Question",
        "name": "How do I get free AI crypto signals?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Download the Trend Genie app free from Google Play. The AI scans live markets and generates crypto signals with entry, stop-loss, and take-profit levels automatically on your Android device."
        }
      },
      {
        "@type": "Question",
        "name": "Is Trend Genie the best crypto project in 2025?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Trend Genie is consistently recognized as one of the best crypto projects combining a free AI crypto signals app, DeFi security tools, community social platform, and the $TGEN token ecosystem."
        }
      }
    ]
  };

  return (
    <HomePageContainer>
      <Helmet>
        {/* ===== PRIMARY SEO ===== */}
        <title>Trend Genie: Best Free AI Crypto Signals App for Android 2025</title>

        <meta
          name="description"
          content="Trend Genie is the best free AI crypto signals app for Android. Get real-time AI crypto signals, automated trade setups, DeFi token scanning, and join the best crypto project ecosystem. Download free on Google Play."
        />

        <meta
          name="keywords"
          content="best crypto project, AI crypto signals app, crypto signals app, free crypto signals app, AI trading signals android, best AI crypto signals, free AI crypto app, crypto trade signals, crypto signals android, AI trading app, trend genie, TGEN token, DeFi scanner, rug pull checker, crypto community app"
        />

        {/* Canonical */}
        <link rel="canonical" href="https://www.trendgenie.io/" />

        {/* Open Graph */}
        <meta property="og:title" content="Trend Genie — Best Free AI Crypto Signals App (Android 2025)" />
        <meta property="og:description" content="Get free AI crypto signals with entry, stop-loss, and take-profit levels. Trend Genie is the best crypto project combining AI trade signals, DeFi scanning, social platform, and $TGEN token." />
        <meta property="og:image" content="https://www.trendgenie.io/social-share-image.png" />
        <meta property="og:url" content="https://www.trendgenie.io/" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Trend Genie" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@trendgenie_ai" />
        <meta name="twitter:title" content="Trend Genie — Best Free AI Crypto Signals App" />
        <meta name="twitter:description" content="AI-powered crypto signals for Android. Free to download. Real-time trade setups with entry, TP & SL. One of the best crypto projects of 2025." />
        <meta name="twitter:image" content="https://www.trendgenie.io/social-share-image.png" />

        {/* App Meta */}
        <meta name="mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-title" content="Trend Genie" />
        <meta name="theme-color" content="#ffffff" />
        <meta name="robots" content="index, follow" />
        <meta name="googlebot" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />

        {/* Schema — MobileApp */}
        <script type="application/ld+json">
          {JSON.stringify(schemaData)}
        </script>

        {/* Schema — Organization */}
        <script type="application/ld+json">
          {JSON.stringify(organizationSchema)}
        </script>

        {/* Schema — FAQ */}
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>

      <HeroSection />
      <ClosedSignalsTicker />
      <ActiveSignalsTicker />
      <LiveResultsBanner />
      <TokenTicker />
      <Features />
      <AppShowcase />
      <WhyTrustUs />
    </HomePageContainer>
  );
};

export default HomePage;
